// Iter-194i — Drive auto-provisioning for new clients (CEO-only).
// Lets the CEO configure:
//   • The default Drive parent folder URL where every new client gets a
//     "<NN>-<Client Name>" subfolder auto-created (with the canonical
//     001-Devis / 002-BDC / 003-CRA subtree provisioned right after).
//   • The starting number used by the in-DB counter (anti-collided against
//     the actual Drive listing on each creation).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderTree, Save, Hash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function ClientDriveAutoProvisioningBlock() {
  const [url, setUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [folderId, setFolderId] = useState("");
  const [counter, setCounter] = useState("");
  const [savedCounter, setSavedCounter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/settings");
      const u = r.data?.default_client_drive_parent_url || "";
      const id = r.data?.default_client_drive_parent_id || "";
      const c = String(r.data?.next_client_folder_number ?? 100);
      setUrl(u); setSavedUrl(u);
      setFolderId(id);
      setCounter(c); setSavedCounter(c);
    } catch (e) { toast.error("Échec chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dirty = url.trim() !== savedUrl.trim() || String(counter).trim() !== savedCounter.trim();

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (url.trim() !== savedUrl.trim()) payload.default_client_drive_parent_url = url.trim();
      const n = parseInt(counter, 10);
      if (!Number.isNaN(n) && String(n) !== savedCounter) {
        if (n < 1 || n > 99999) { toast.error("Numéro 1..99999"); setSaving(false); return; }
        payload.next_client_folder_number = n;
      }
      const r = await api.put("/settings", payload);
      setSavedUrl(r.data?.default_client_drive_parent_url || "");
      setFolderId(r.data?.default_client_drive_parent_id || "");
      setSavedCounter(String(r.data?.next_client_folder_number ?? 100));
      toast.success("Paramétrage Drive enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  const enabled = !!savedUrl;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="client-drive-autoprov-block">
      <div className="flex items-center gap-3">
        <FolderTree size={18} className="text-zinc-700" />
        <h2 className="font-heading font-bold text-lg">Drive · Auto-provisioning des nouveaux clients</h2>
        <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${enabled ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`}>
          {enabled ? "Activé" : "Désactivé"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
        Quand un dossier Drive parent par défaut est défini, chaque nouveau client est
        automatiquement créé en tant que sous-dossier <code className="px-1 py-0.5 bg-zinc-100 rounded font-mono">{"{NN}-{Nom du client}"}</code> dans
        ce dossier, avec l'arborescence canonique <code className="px-1 py-0.5 bg-zinc-100 rounded font-mono">001-Devis / 002-BDC / 003-CRA</code> provisionnée
        automatiquement. À chaque création de mission, le sous-arbre par mission est
        également créé dans 002-BDC et 003-CRA.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">URL du dossier Drive parent</Label>
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/1ABCxyz..."
            data-testid="client-drive-parent-url"
          />
          {folderId && (
            <div className="text-[10px] font-mono text-zinc-500 mt-1">ID extrait : {folderId}</div>
          )}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
            <Hash size={11} /> Prochain numéro de dossier
          </Label>
          <Input
            type="number" min={1} max={99999}
            value={counter}
            onChange={(e) => setCounter(e.target.value)}
            data-testid="client-drive-next-number"
          />
          <div className="text-[10px] text-zinc-500 mt-1">
            Anti-collision automatique : si un dossier avec ce numéro (ou supérieur) existe
            déjà dans le Drive parent, le compteur est ré-aligné lors de la prochaine création.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="client-drive-autoprov-save"
          className="inline-flex items-center gap-1.5 px-4 h-9 bg-zinc-950 text-white text-sm rounded hover:bg-zinc-800 disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        {enabled && (
          <span className="text-xs text-zinc-500">
            Pour désactiver l'auto-provisioning, videz le champ URL puis enregistrez.
          </span>
        )}
      </div>
    </div>
  );
}
